/**
 * InfoModal.js — Reusable styled modal for registration/upgrade/limitation/ad messages.
 *
 * Props:
 * - title:     string — bold heading text
 * - message:   string — body text (supports \n for line breaks)
 * - type:      string — 'info' | 'warning' | 'premium' | 'ad' (controls accent + icon)
 * - onDismiss: () => void — called when user clicks Dismiss/Continue or backdrop
 * - dismissLabel: string — footer button label (default 'OK')
 * - ads:       { enabled, client, slotSave, slotLoad } | null — ad config; when
 *              type === 'ad' and ads.enabled, a Google AdSense unit renders
 * - slot:      string — which ad slot to show ('save' | 'load')
 * - onUpgrade: () => void — optional; when type === 'ad', renders a
 *              "Go ad-free with Premium" button
 *
 * Usage:
 *   {infoModal && <InfoModal {...infoModal} onDismiss={() => setInfoModal(null)} />}
 */
import AdUnit from './AdUnit';
import { useI18n } from './i18n';

const COLORS = {
  info:    { accent: '#2563eb', icon: 'M12 16V12M12 8H12.01M22 12C22 17.5228 17.5228 22 12 22C6.47715 22 2 17.5228 2 12C2 6.47715 6.47715 2 12 2C17.5228 2 22 6.47715 22 12Z' },
  warning: { accent: '#f59e0b', icon: 'M12 9V13M12 17H12.01M10.29 3.86L1.82 18C1.64537 18.3024 1.55296 18.6453 1.55199 18.9945C1.55101 19.3437 1.64149 19.6871 1.81445 19.9905C1.9874 20.2939 2.23673 20.5467 2.53773 20.7239C2.83873 20.9012 3.18083 20.9966 3.53 21H20.47C20.8192 20.9966 21.1613 21.9012 21.4623 20.7239C21.7633 20.5467 22.0126 20.2939 22.1856 19.9905C22.3585 19.6871 22.449 19.3437 22.448 18.9945C22.447 18.6453 22.3546 18.3024 22.18 18L13.71 3.86C13.5317 3.56611 13.2807 3.32312 12.9812 3.154C12.6817 2.98488 12.3437 2.89502 12 2.89502C11.6563 2.89502 11.3183 2.98488 11.0193 3.154C10.7193 3.32312 10.4683 3.56611 10.29 3.86Z' },
  premium: { accent: '#22c55e', icon: 'M12 2L15.09 8.26L22 9.27L17 14.14L18.18 21.02L12 17.77L5.82 21.02L7 14.14L2 9.27L8.91 8.26L12 2Z' },
  ad:      { accent: '#f59e0b', icon: 'M4 11V5H20V11H4ZM4 13V19H20V13H4ZM5 9H9V7H5V9ZM5 15H9V13H5V15ZM12 9H19V7H12V9ZM12 15H19V13H12V15Z' },
};

export default function InfoModal({ title, message, type = 'info', onDismiss, dismissLabel, ads, slot, onUpgrade, adFreeLabel, continueLabel }) {
  // The caller supplies the headline and the body; the small words at the
  // bottom are ours, so they are ours in all three languages too.
  const { t } = useI18n();
  const c = COLORS[type] || COLORS.info;
  const isAd = type === 'ad';
  const adSlot = slot === 'save' ? (ads?.slotSave || '') : (ads?.slotLoad || '');
  const showAdUnit = isAd && ads?.enabled && ads?.client;

  const backdropStyle = {
    position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)', zIndex: 1000,
    display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '1rem',
  };

  const modalStyle = {
    background: '#1e293b', border: `1px solid ${c.accent}`, borderRadius: '12px',
    width: '100%', maxWidth: '440px',
    display: 'flex', flexDirection: 'column',
    // Never taller than the visible viewport: on short landscape screens the
    // modal shrinks to fit and the body scrolls instead of overflowing.
    // Uses the dvh-backed variable so the bottom stays above the browser chrome.
    maxHeight: 'calc(var(--kv-viewport-h) - 24px)',
    overflowX: 'hidden',
    overflowY: 'auto',
    boxShadow: `0 8px 32px rgba(0,0,0,0.5), 0 0 0 1px ${c.accent}33`,
  };

  return (
    <div style={backdropStyle} onClick={(e) => { if (e.target === e.currentTarget) onDismiss(); }}>
      <div style={modalStyle} onClick={(e) => e.stopPropagation()}
        role="dialog" aria-modal="true" aria-label={title}>
        {/* Header */}
        <div style={{
          display: 'flex', alignItems: 'center', gap: '0.75rem',
          padding: '1.1rem 1.25rem', borderBottom: '1px solid #334155',
        }}>
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke={c.accent}
               strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}>
            <path d={c.icon} />
          </svg>
          <h3 style={{ margin: 0, fontSize: '1.05rem', color: '#f1f5f9', fontWeight: 700 }}>{title}</h3>
        </div>

        {/* Body */}
        <div style={{ padding: '1.1rem 1.25rem' }}>
          <p style={{
            margin: 0, fontSize: '0.9rem', lineHeight: 1.6, color: '#94a3b8', whiteSpace: 'pre-wrap',
          }}>
            {message}
          </p>
          {showAdUnit && <AdUnit client={ads.client} slot={adSlot} />}
        </div>

        {/* Footer */}
        <div style={{
          padding: '0.75rem 1.25rem 1.1rem', borderTop: '1px solid #334155',
          display: 'flex', justifyContent: 'flex-end', gap: '0.5rem', flexWrap: 'wrap',
        }}>
          {isAd && onUpgrade && (
            <button type="button" onClick={onUpgrade}
              style={{
                padding: '0.5rem 1.25rem', borderRadius: '6px', cursor: 'pointer',
                fontSize: '0.85rem', fontWeight: 700, border: '1px solid #f59e0b',
                background: '#f59e0b', color: '#0f172a', marginRight: 'auto',
              }}>
              ⬆ {adFreeLabel || t('info.adFree')}
            </button>
          )}
          <button type="button" onClick={onDismiss}
            style={{
              padding: '0.5rem 1.25rem', borderRadius: '6px', cursor: 'pointer',
              fontSize: '0.85rem', fontWeight: 600, border: `1px solid ${c.accent}`,
              background: 'transparent', color: c.accent,
            }}>
            {dismissLabel || (isAd ? (continueLabel || t('info.continue')) : t('info.ok'))}
          </button>
        </div>
      </div>
    </div>
  );
}
